import React from 'react';
import { PlusCircle, ShoppingBag, Truck, Package } from 'lucide-react';
import { useLanguage } from '../../../features/language/useLanguage';
import { usePermissions } from '../../../hooks/usePermissions';

type QuickActionKey = 'create-so' | 'create-po' | 'create-wo' | 'add-product';

interface QuickActionsProps {
  onAction: (action: QuickActionKey) => void;
}

interface ActionDef {
  key: QuickActionKey;
  permission: { obj: string; act: string };
  name: string;
  fullName: string;
  icon: React.ReactNode;
  gradient: string;
  baseColor: string;
  textColor: string;
}

export const QuickActions: React.FC<QuickActionsProps> = ({ onAction }) => {
  const { t } = useLanguage();
  const { can } = usePermissions();

  const allActions: ActionDef[] = [
    {
      key: 'create-so',
      permission: { obj: 'sales_orders', act: 'write' },
      name: t('dashboard.quickActions.createSO'),
      fullName: t('dashboard.quickActions.salesOrder'),
      icon: <ShoppingBag className="h-5 w-5" />,
      gradient: 'from-blue-500 to-blue-600',
      baseColor: 'bg-blue-500',
      textColor: 'text-blue-600',
    },
    {
      key: 'create-po',
      permission: { obj: 'purchase_orders', act: 'write' },
      name: t('dashboard.quickActions.createPO'),
      fullName: t('dashboard.quickActions.purchaseOrder'),
      icon: <Truck className="h-5 w-5" />,
      gradient: 'from-indigo-500 to-indigo-600',
      baseColor: 'bg-indigo-500',
      textColor: 'text-indigo-600',
    },
    {
      key: 'create-wo',
      permission: { obj: 'work_orders.roasting', act: 'write' },
      name: t('dashboard.quickActions.createWO'),
      fullName: t('dashboard.quickActions.workOrder'),
      icon: <PlusCircle className="h-5 w-5" />,
      gradient: 'from-purple-500 to-purple-600',
      baseColor: 'bg-purple-500',
      textColor: 'text-purple-600',
    },
    {
      key: 'add-product',
      permission: { obj: 'inventory', act: 'write' },
      name: t('dashboard.quickActions.addProduct'),
      fullName: t('dashboard.quickActions.inventory'),
      icon: <Package className="h-5 w-5" />,
      gradient: 'from-emerald-500 to-emerald-600',
      baseColor: 'bg-emerald-500',
      textColor: 'text-emerald-600',
    },
  ];

  const actions = allActions.filter((a) => can(a.permission.obj, a.permission.act));

  if (actions.length === 0) {
    return null;
  }

  // Adaptive grid — balance single/dual/triple/full layouts.
  // 1 → one tile full-width; 2 → side-by-side; 3 → three across; 4 → 2×2.
  const gridColsClass =
    actions.length === 1 ? 'grid-cols-1'
      : actions.length === 3 ? 'grid-cols-3'
        : 'grid-cols-2';

  return (
    <div className="bg-white p-6 rounded-[24px] border border-gray-100 shadow-sm">
      <h3 className="text-md font-bold text-gray-700 uppercase tracking-wider mb-4 px-1">
        {t('dashboard.quickActions.title')}
      </h3>

      <div className={`grid ${gridColsClass} gap-3`}>
        {actions.map((action) => (
          <button
            key={action.key}
            onClick={() => onAction(action.key)}
            className="group relative flex flex-col items-center justify-center p-5 rounded-2xl transition-all duration-300 active:scale-95 overflow-hidden border border-transparent hover:shadow-lg"
          >
            {/* Background Layer: Default (Opacity 15%) */}
            <div className={`absolute inset-0 ${action.baseColor} opacity-[0.55] group-hover:opacity-0 transition-opacity duration-300`} />

            {/* Hover Layer: Full Gradient */}
            <div className={`absolute inset-0 bg-gradient-to-br ${action.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />

            {/* Content Container */}
            <div className="relative z-10 flex flex-col items-center">
              {/* Icon Container */}
              <div className="mb-3 p-2.5 rounded-xl bg-white shadow-sm transition-all duration-300 group-hover:bg-white/20 group-hover:shadow-none group-hover:scale-110">
                <div className={`${action.textColor} group-hover:text-white transition-colors duration-300`}>
                  {action.icon}
                </div>
              </div>

              <span className="text-[13px] font-bold text-gray-800 group-hover:text-white transition-colors duration-300 text-center leading-tight">
                {action.name}
              </span>
              <span className="text-[10px] font-medium text-gray-800 group-hover:text-white/70 transition-colors duration-300 hidden sm:block">
                {action.fullName}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
